import { TableProps } from 'antd';
import dayjs from 'dayjs';
import { formatDate, localize } from 'mixins/helpers';

const getBeginDateColumn = (translations: Record<string, string>) => ({
    title: localize('find-taxpayer-service-registration-date', translations),
    dataIndex: 'beginDate',
    key: 'beginDate',
    render: (beginDate) => formatDate(beginDate),
});

const getEndDateColumn = (translations: Record<string, string>) => ({
    title: localize('find-taxpayer-service-de-registration-date', translations),
    dataIndex: 'endDate',
    key: 'endDate',
    render: (endDate) => formatDate(endDate),
});

const getEndReasonColumn = (translations: Record<string, string>, lang: string) => ({
    title: localize('find-taxpayer-service-de-registration-reasons', translations),
    dataIndex: 'endReason',
    key: 'endReason',
    render: (endReason) => (endReason ? endReason[lang] : '-'),
});

const getSuspensionPeriodColumn = (translations: Record<string, string>) => ({
    title: localize('find-taxpayer-service-activity-suspension-period', translations),
    dataIndex: 'suspensionPeriod',
    key: 'suspensionPeriod',
    render: (suspensionPeriod) => suspensionPeriod || '-',
});

const getAdditionalInfoDocumentColumn = (translations: Record<string, string>) => ({
    title: localize('find-taxpayer-service-additional-info-document', translations),
    dataIndex: 'additionalInfoDocument',
    key: 'additionalInfoDocument',
    render: (additionalInfoDocument) =>
        additionalInfoDocument
            ? `№${additionalInfoDocument.number} ${additionalInfoDocument.date}`
            : '-',
});

const getLzchpTypeColumn = (translations: Record<string, string>) => ({
    title: localize('type', translations),
    dataIndex: 'lzchpType',
    key: 'lzchpType',
    render: (lzchpType) =>
        lzchpType
            ? localize(`find-taxpayer-service-lzchp-type-${lzchpType}`, translations)
            : localize('find-taxpayer-service-lzchp-type-not-specified', translations),
});

const getRowNumberColumn = () => ({
    title: '№',
    dataIndex: 'rowNumber',
    key: 'rowNumber',
    render: (rowNumber) => rowNumber,
});

const getIinBinColumn = (translations: Record<string, string>) => ({
    title: localize('iin-bin', translations),
    dataIndex: 'iinBin',
    key: 'iinBin',
    render: (iinBin) => iinBin,
});

const getTypeColumn = (translations: Record<string, string>) => ({
    title: localize('type', translations),
    dataIndex: 'type',
    key: 'type',
    render: (type) => (type ? localize(`${TAXPAYER_TYPE_PREFIX}-${type}`, translations) : '-'),
});

const getNameColumn = (translations: Record<string, string>) => ({
    title: localize('title', translations),
    dataIndex: 'name',
    key: 'name',
    render: (name) => name,
});
export const columns = (
    translations: Record<string, string>,
    lang: string,
    type: string,
    rowCount: number
): TableProps['columns'] => {
    const showUlIpCardInfo = rowCount > 1;

    switch (type) {
        case 'FL':
            return [getBeginDateColumn(translations)];
        case 'UL':
            const ulColumns = [
                getBeginDateColumn(translations),
                getEndDateColumn(translations),
                getEndReasonColumn(translations, lang),
            ];
            if (showUlIpCardInfo) {
                ulColumns.unshift(
                    ...[
                        getRowNumberColumn(),
                        getNameColumn(translations),
                        getTypeColumn(translations),
                        getIinBinColumn(translations),
                    ]
                );
            }
            return ulColumns;
        case 'IP':
            const ipColumns = [
                getBeginDateColumn(translations),
                getEndDateColumn(translations),
                getEndReasonColumn(translations, lang),
                getSuspensionPeriodColumn(translations),
                getAdditionalInfoDocumentColumn(translations),
            ];
            if (showUlIpCardInfo) {
                ipColumns.unshift(
                    ...[
                        getRowNumberColumn(),
                        getNameColumn(translations),
                        getTypeColumn(translations),
                        getIinBinColumn(translations),
                    ]
                );
            }
            return ipColumns;
        case 'LZCHP':
            return [
                getLzchpTypeColumn(translations),
                getBeginDateColumn(translations),
                getEndDateColumn(translations),
                getEndReasonColumn(translations, lang),
                getSuspensionPeriodColumn(translations),
            ];
        default:
            return [];
    }
};

export const TAXPAYER_TYPE_PREFIX = 'find-taxpayer-service-taxpayer-type';

export const RESULTS_LIMIT = 150;

export const IDENTIFIER_TYPE_PREFIX = 'find-taxpayer-service';
export const IDENTIFIER_TYPE_MAP = {
    FL: 'iin',
    IP: 'iin-bin',
    IPS: 'iin-bin',
    UL: 'bin',
    UL_NR: 'bin',
    FL_NR: 'iin',
    LZCHP: 'iin',
};

export const paymentColumns = (
    translations: Record<string, string>
): TableProps['columns'] => {
    const thisYear = dayjs().year();
    const paymentColumns: TableProps['columns'] = [];

    for (let y = thisYear - 5; y < thisYear; y++) {
        paymentColumns.push({
            title: localize(
                'find-taxpayer-service-tax-revenues-in-n-year',
                translations,
                undefined,
                y
            ),
            dataIndex: y,
            render: (sum: number) => sum?.toFixed(2),
        });
    }

    const getTypeNeedle = (type: string) => {
        switch (type) {
            case 'tax':
                return 'find-taxpayer-service-tax-revenues';
            case 'penalty':
                return 'find-taxpayer-service-penalty';
            case 'fine':
                return 'find-taxpayer-service-fine';
            default:
                return type;
        }
    };

    return [
        {
            title: localize('find-taxpayer-service-payment-type', translations),
            dataIndex: 'type',
            render: (type) => localize(getTypeNeedle(type), translations),
        },
        ...paymentColumns,
        {
            title: localize(
                'find-taxpayer-service-tax-revenues-period',
                translations,
                undefined,
                thisYear
            ),
            dataIndex: thisYear,
            render: (sum: number) => sum?.toFixed(2),
        },
    ];
};

export const ndsRevenueColumns = (
    translations: Record<string, string>
): TableProps['columns'] => {
    const thisYear = dayjs().year();
    const paymentColumns: TableProps['columns'] = [];

    for (let y = thisYear - 5; y < thisYear; y++) {
        paymentColumns.push({
            title: localize(
                'find-taxpayer-service-vat-refund',
                translations,
                undefined,
                y
            ),
            dataIndex: y,
            render: (sum: number) => sum?.toFixed(2),
        });
    }

    return [
        {
            title: localize('find-taxpayer-service-payment-type', translations),
            render: () =>
                localize(
                    'find-taxpayer-service-vat-refund-amount',
                    translations
                ),
        },
        ...paymentColumns,
        {
            title: localize(
                'find-taxpayer-service-vat-refund-period',
                translations,
                undefined,
                thisYear
            ),
            dataIndex: thisYear,
            render: (sum: number) => sum?.toFixed(2),
        },
    ];
};